import { useEffect } from 'react';

/**
 * Removes stale `pointer-events: none` left on `<body>` by Radix UI portals
 * (Dropdown / Dialog / Sheet / AlertDialog / Popover) whose close animation
 * gets interrupted. Without this guard, an aborted close cycle can leave the
 * whole page unclickable until a hard reload — every `Link`, button, and form
 * control on the document silently swallows clicks.
 *
 * The pattern this catches: dropdown closes via a programmatic navigation or
 * a dropdown→dialog hand-off, the close animation gets cancelled mid-flight,
 * Radix never fires its body-lock cleanup, and `body.style.pointerEvents`
 * stays `'none'` indefinitely. See docs/pointer-events-cleanup.md.
 *
 * Mount exactly once at the app root.
 */
export function usePointerEventsCleanup(): void {
  useEffect(() => {
    const body = document.body;

    /** Strip the body lock when it's set but no open Radix overlay owns it. */
    function maybeUnlock(): void {
      if (body.style.pointerEvents !== 'none') return;
      // Radix marks every open overlay with `data-state="open"`. If any such
      // element exists, the lock is legitimate and must stay.
      if (document.querySelector('[data-state="open"]')) return;
      body.style.pointerEvents = '';
    }

    const observer = new MutationObserver(maybeUnlock);

    // Body style changes — fires when Radix sets/clears the lock directly.
    observer.observe(body, {
      attributes: true,
      attributeFilter: ['style'],
    });

    // `data-state` flips across the subtree — fires when an overlay
    // transitions open↔closed. Filter is narrow so this stays cheap; most DOM
    // mutations don't touch `data-state`.
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-state'],
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, []);
}
